import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'react-hot-toast';
import { Plus, Trash2 } from 'lucide-react';
import { rbacApi } from '../../services/rbacApi';

const RoleProfilesTab = () => {
  const [profiles, setProfiles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', roles: [], description: '' });

  const load = () => {
    rbacApi.listProfiles().then((r) => setProfiles(r.data)).catch(() => toast.error('Failed to load profiles'));
    rbacApi.listRoles().then((r) => setRoles(r.data));
  };
  useEffect(() => { load(); }, []);

  const toggleRole = (name) =>
    setForm((f) => ({ ...f, roles: f.roles.includes(name) ? f.roles.filter((r) => r !== name) : [...f.roles, name] }));

  const save = async () => {
    try { await rbacApi.createProfile(form); toast.success('Role profile created'); setOpen(false); setForm({ name: '', roles: [], description: '' }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (p) => {
    try { await rbacApi.deleteProfile(p.role_profile_id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="profiles-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">{profiles.length} profile(s) — bundle roles for one-click assignment</p>
        <Button className="bg-orange-600 hover:bg-orange-700" onClick={() => setOpen(true)} data-testid="add-profile-btn">
          <Plus className="w-4 h-4 mr-1" />New Profile
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {profiles.map((p) => (
          <div key={p.role_profile_id} className="border rounded-lg p-4" data-testid={`profile-card-${p.name}`}>
            <div className="flex justify-between items-start">
              <div className="font-semibold">{p.name}</div>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(p)}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(p.roles || []).map((r) => (
                <span key={r} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">{r}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="profile-dialog">
          <DialogHeader><DialogTitle>New Role Profile</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></div>
            <div>
              <Label>Roles in this profile</Label>
              <div className="mt-1 max-h-44 overflow-y-auto border rounded p-2 grid grid-cols-2 gap-1">
                {roles.map((r) => (
                  <label key={r.role_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.roles.includes(r.name)} onChange={() => toggleRole(r.name)} data-testid={`profile-role-${r.name}`} />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={save} data-testid="save-profile-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleProfilesTab;
